import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SyncedHScroll } from '@/components/shared/SyncedHScroll';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { api, formatApiError } from '@/lib/api';
import { useSort } from '@/lib/useSort';
import { SortableHeader } from '@/components/shared/SortableHeader';
import { toast } from 'sonner';
import { Eye, Pencil, Archive, Trash2, Search, Loader2, ShieldAlert, RotateCcw } from 'lucide-react';

// Canonical assessment types actually present in library data — no invented taxonomy.
export const ASSESSMENT_TYPES = ['psychometric', 'competency', 'knowledge', 'climate', 'certification', 'behavioral', '360', 'other'];

const LANGUAGE_LABELS = { es: 'Español', spanish: 'Español', en: 'English', english: 'English' };

// Browse-by-type layer shared by Tarjetas & Lista (writes the same URL param
// the LibrarySearchExplorer already filters on — filters compose, one taxonomy).
export const LibraryTypeBar = ({ items }) => {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const current = params.get('assessment_type') || 'all';
  const counts = useMemo(() => {
    const c = {};
    (items || []).forEach((it) => {
      if (it.status !== 'published') return;
      const k = it.assessment_type || 'none';
      c[k] = (c[k] || 0) + 1;
      c.all = (c.all || 0) + 1;
    });
    return c;
  }, [items]);
  const label = (k) => k === 'all' ? t('library.typeBar.all', { defaultValue: 'Todas' })
    : k === 'none' ? t('library.typeBar.untyped', { defaultValue: 'Sin tipo' })
      : t(`library.typeBar.types.${k}`, { defaultValue: t(`library.search.types.${k}`, { defaultValue: k }) });
  const setType = (k) => {
    const next = new URLSearchParams(params);
    if (k === 'all') next.delete('assessment_type'); else next.set('assessment_type', k);
    setParams(next, { replace: true });
  };
  const visible = ['all', ...ASSESSMENT_TYPES.filter((k) => counts[k]), ...(counts.none ? ['none'] : [])];
  return (
    <Card className="p-3 shadow-card" data-testid="library-type-bar">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">
          {t('library.typeBar.title', { defaultValue: 'Tipo de prueba' })}
        </span>
        {visible.map((k) => (
          <button key={k} onClick={() => setType(k)} data-testid={`library-type-${k}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition flex items-center gap-1.5
                    ${current === k ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-foreground hover:bg-muted/50'}
                    ${k === 'none' ? 'border-dashed' : ''}`}>
            <span className="font-medium">{label(k)}</span>
            <span className={`tabular-nums whitespace-nowrap ${current === k ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
              {counts[k] || 0} {(counts[k] || 0) === 1
                ? t('library.typeBar.activeOne', { defaultValue: 'activa' })
                : t('library.typeBar.activeMany', { defaultValue: 'activas' })}
            </span>
          </button>
        ))}
      </div>
    </Card>
  );
};

const AT_RE = /^(AT autosave [0-9a-f]+|P\d+[BC]\d+ AT [0-9a-f]+)$/i;
const SCRIPTED_RE = /\(sample\)|^Cleanish$|^PubFlow$/i;

export const originOf = (q) => {
  const n = q.name || '';
  if (AT_RE.test(n)) return 'auto_test';
  if (SCRIPTED_RE.test(n)) return 'uat';
  return 'manual';
};

const STATUS_TONES = {
  published: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  submitted_for_review: 'bg-amber-100 text-amber-800 border-amber-200',
  needs_revision: 'bg-rose-100 text-rose-800 border-rose-200',
  approved: 'bg-sky-100 text-sky-800 border-sky-200',
  archived: 'bg-zinc-100 text-zinc-600 border-zinc-200',
};

const fmtDate = (iso, lang) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(lang === 'en' ? 'en-US' : 'es-PA', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

export const LibraryListView = ({ items, isAdmin, statusFilter, onStatusFilter, reload }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const typeFilter = params.get('assessment_type') || 'all';
  // UAT 3.7 — Cards/List filter parity: suite, language and search share the
  // same URL params as the Cards explorer, so they persist across the toggle.
  const suiteF = params.get('suite') || 'all';
  const langF = params.get('language') || 'all';
  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (!val || val === 'all') next.delete(key); else next.set(key, val);
    setParams(next, { replace: true });
  };
  const [usage, setUsage] = useState({ usage: {}, authors: {} });
  const [q, setQ] = useState(params.get('q') || '');
  const [origin, setOrigin] = useState('all');
  const [used, setUsed] = useState('all');
  const [groupBy, setGroupBy] = useState('none');
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedItems, setArchivedItems] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null); // {item, dep} | {bulk:true, rows}
  const sort = useSort();

  useEffect(() => {
    if (showArchived && archivedItems === null) {
      api.get('/admin/questionnaires', { params: { include_archived: true } })
        .then(({ data }) => setArchivedItems(Array.isArray(data) ? data : data.items || []))
        .catch(() => setArchivedItems([]));
    }
  }, [showArchived, archivedItems]);

  const sourceItems = showArchived && archivedItems ? archivedItems : items;

  useEffect(() => {
    api.get('/admin/questionnaires/usage-summary')
      .then(({ data }) => setUsage(data))
      .catch(() => {});
  }, [items]);

  const enriched = useMemo(() => sourceItems.map((it) => {
    const u = usage.usage?.[it.id] || {};
    return {
      ...it,
      _origin: originOf(it),
      _used: (u.assignments || 0) + (u.responses || 0) > 0,
      _assignments: u.assignments || 0,
      _responses: u.responses || 0,
      _versions: u.versions || 0,
      _author: usage.authors?.[it.created_by] || '—',
      _type: it.assessment_type || 'none',
      _lang: (it.language || '').toLowerCase(),
    };
  }), [sourceItems, usage]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return enriched.filter((it) => {
      if (statusFilter !== 'all') {
        if (statusFilter === 'pending') {
          if (!['submitted_for_review', 'needs_revision', 'approved'].includes(it.status)) return false;
        } else if (it.status !== statusFilter) return false;
      }
      if (typeFilter !== 'all' && it._type !== typeFilter) return false;
      if (suiteF !== 'all' && !(it.suite_codes || []).includes(suiteF)) return false;
      if (langF !== 'all' && it._lang !== langF.toLowerCase()) return false;
      if (origin !== 'all' && it._origin !== origin) return false;
      if (used !== 'all' && it._used !== (used === 'used')) return false;
      if (term && !(`${it.name || ''} ${it.code || ''}`.toLowerCase().includes(term))) return false;
      return true;
    });
  }, [enriched, statusFilter, typeFilter, suiteF, langF, origin, used, q]);

  const sorted = useMemo(() => sort.apply(filtered, {
    name: (x) => (x.name || '').toLowerCase(),
    code: (x) => (x.code || '').toLowerCase(),
    status: (x) => x.status || '',
    versions: (x) => x._versions,
    author: (x) => (x._author || '').toLowerCase(),
    updated: (x) => x.updated_at || x.created_at || '',
  }), [filtered, sort]);

  const groups = useMemo(() => {
    if (groupBy === 'none') return [{ key: null, rows: sorted }];
    const keyOf = (x) => groupBy === 'suite' ? ((x.suite_codes || [])[0] || '—')
      : groupBy === 'status' ? x.status
        : groupBy === 'type' ? x._type
          : groupBy === 'language' ? (x._lang || '—')
            : groupBy === 'origin' ? x._origin
              : groupBy === 'author' ? x._author : '—';
    const map = new Map();
    sorted.forEach((x) => {
      const k = keyOf(x);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(x);
    });
    return Array.from(map.entries()).map(([key, rows]) => ({ key, rows }));
  }, [sorted, groupBy]);

  const selIds = Object.keys(selected).filter((k) => selected[k]);
  const toggleAll = (rows, val) => {
    const next = { ...selected };
    rows.forEach((r) => { next[r.id] = val; });
    setSelected(next);
  };

  const originLabel = (o) => t(`library.list.origin.${o}`, {
    defaultValue: o === 'auto_test' ? 'Automated test' : o === 'uat' ? 'UAT / Test' : 'Manual',
  });
  const statusLabel = (s) => t(`library.lifecycle.statuses.${s}`, { defaultValue: s });
  const typeLabel = (k) => (!k || k === 'none')
    ? t('library.typeBar.untyped', { defaultValue: 'Sin tipo' })
    : t(`library.search.types.${k}`, { defaultValue: k });
  const langLabel = (l) => LANGUAGE_LABELS[l] || (l || '—');

  const setType = async (it, val) => {
    try {
      await api.put(`/admin/questionnaires/${it.id}`, { assessment_type: val === 'none' ? null : val });
      toast.success(t('library.list.typeUpdated', { defaultValue: 'Tipo actualizado' }), { description: it.name });
      setArchivedItems(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const restoreOne = async (it) => {
    setBusy(true);
    try {
      await api.post(`/admin/trash/questionnaire/${it.id}/restore`);
      toast.success(t('trash.restoredToast', { defaultValue: 'Restored' }), { description: it.name });
      setArchivedItems(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const archiveOne = async (it) => {
    setBusy(true);
    try {
      await api.post(`/admin/archive/questionnaire/${it.id}`, { reason_code: 'manual_archive' });
      toast.success(t('toasts.archived', { defaultValue: 'Archived' }), { description: it.name });
      reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const preflightDelete = async (it) => {
    setBusy(true);
    try {
      const { data: dep } = await api.get(`/admin/trash/questionnaire/${it.id}/dependencies`);
      if (dep.safe) setConfirmDel({ item: it, dep });
      else {
        const parts = dep.blockers.map((b) => `${b.count} ${t(`library.list.blocker.${b.kind}`, { defaultValue: b.kind.replace(/_/g, ' ') })}`).join(', ');
        toast.error(t('library.list.blockedTitle', { defaultValue: 'Cannot delete permanently' }), {
          description: t('library.list.blockedDesc', { defaultValue: 'This evaluation has {{deps}}. You can archive it instead.', deps: parts }),
        });
      }
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const bulkPreflight = async () => {
    setBusy(true);
    try {
      const rows = enriched.filter((x) => selIds.includes(x.id));
      const results = await Promise.all(rows.map(async (r) => {
        try {
          const { data } = await api.get(`/admin/trash/questionnaire/${r.id}/dependencies`);
          return { row: r, dep: data };
        } catch { return { row: r, dep: { safe: false, blockers: [] } }; }
      }));
      setConfirmDel({ bulk: true, rows: results });
    } finally { setBusy(false); }
  };

  const executeDelete = async () => {
    setBusy(true);
    try {
      if (confirmDel.bulk) {
        const deletable = confirmDel.rows.filter((r) => r.dep.safe);
        let ok = 0;
        for (const r of deletable) {
          try { await api.delete(`/admin/trash/questionnaire/${r.row.id}`); ok += 1; } catch { /* audited server-side */ }
        }
        toast.success(t('library.list.bulkDeleted', { defaultValue: '{{n}} evaluations permanently deleted (backup + audit).', n: ok }));
        setSelected({});
      } else {
        await api.delete(`/admin/trash/questionnaire/${confirmDel.item.id}`);
        toast.success(t('library.list.deleted', { defaultValue: 'Permanently deleted (backup + audit).' }), { description: confirmDel.item.name });
      }
      setConfirmDel(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const STATUS_CHIPS = ['all', 'published', 'pending', 'draft', ...(showArchived ? ['archived'] : [])];

  const renderRow = (it) => (
    <TableRow key={it.id} data-testid={`library-list-row-${it.id}`} className={selected[it.id] ? 'bg-primary/5' : ''}>
      {isAdmin && (
        <TableCell className="w-8">
          <Checkbox checked={!!selected[it.id]} onCheckedChange={(v) => setSelected({ ...selected, [it.id]: !!v })} data-testid={`library-select-${it.id}`} />
        </TableCell>
      )}
      <TableCell>
        <button className="text-left font-medium text-primary hover:underline" onClick={() => navigate(`/admin/questionnaires/${it.id}`)} data-testid={`library-open-${it.id}`}>
          {it.name || '—'}
        </button>
      </TableCell>
      <TableCell className="text-xs font-mono text-muted-foreground">{it.code || '—'}</TableCell>
      <TableCell className="text-xs">{(it.suite_codes || []).join(', ') || '—'}</TableCell>
      <TableCell>
        <Badge variant="outline" className={`text-xs ${STATUS_TONES[it.status] || ''}`}>{statusLabel(it.status)}</Badge>
      </TableCell>
      <TableCell>
        {isAdmin ? (
          <Select value={it._type} onValueChange={(v) => setType(it, v)}>
            <SelectTrigger className={`h-7 w-36 text-xs ${it._type === 'none' ? 'border-amber-300 text-amber-700' : ''}`}
                           data-testid={`library-type-select-${it.id}`}>
              <SelectValue>{typeLabel(it._type)}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {ASSESSMENT_TYPES.map((k) => <SelectItem key={k} value={k}>{typeLabel(k)}</SelectItem>)}
              <SelectItem value="none">{typeLabel('none')}</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <Badge variant="outline" className={`text-xs ${it._type === 'none' ? 'bg-amber-50 text-amber-800 border-amber-200' : ''}`}>
            {typeLabel(it._type)}
          </Badge>
        )}
      </TableCell>
      <TableCell className="text-xs tabular-nums text-center">{it._versions}</TableCell>
      <TableCell className="text-xs">{it._author}</TableCell>
      <TableCell>
        <Badge variant="outline" className={`text-xs ${it._origin === 'manual' ? '' : 'bg-amber-50 text-amber-800 border-amber-200'}`}
               title={t('library.list.originHint', { defaultValue: 'Heuristic based on naming patterns and creation batch — informative only.' })}>
          {originLabel(it._origin)}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={`text-xs ${it._used ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'text-muted-foreground'}`}>
          {it._used
            ? t('library.list.used', { defaultValue: 'Used ({{a}})', a: it._assignments })
            : t('library.list.neverUsed', { defaultValue: 'Never used' })}
        </Badge>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(it.updated_at || it.created_at, i18n.language)}</TableCell>
      <TableCell className="text-right whitespace-nowrap">
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title={t('common.view', { defaultValue: 'View' })}
                onClick={() => navigate(`/admin/questionnaires/${it.id}`)} data-testid={`library-view-${it.id}`}>
          <Eye className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Studio"
                onClick={() => navigate(`/admin/studio/edit/${it.id}`)} data-testid={`library-studio-${it.id}`}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        {isAdmin && it.status !== 'archived' && (
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title={t('library.list.archive', { defaultValue: 'Archive' })}
                  disabled={busy} onClick={() => archiveOne(it)} data-testid={`library-archive-${it.id}`}>
            <Archive className="h-3.5 w-3.5" />
          </Button>
        )}
        {isAdmin && it.status === 'archived' && (
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-emerald-700" title={t('trash.restore', { defaultValue: 'Restore' })}
                  disabled={busy} onClick={() => restoreOne(it)} data-testid={`library-restore-${it.id}`}>
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        )}
        {isAdmin && (
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                  title={t('library.list.deletePermanently', { defaultValue: 'Delete permanently' })}
                  disabled={busy} onClick={() => preflightDelete(it)} data-testid={`library-delete-${it.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );

  const headers = (
    <TableRow>
      {isAdmin && (
        <TableHead className="w-8">
          <Checkbox checked={sorted.length > 0 && sorted.every((r) => selected[r.id])}
                    onCheckedChange={(v) => toggleAll(sorted, !!v)} data-testid="library-select-all" />
        </TableHead>
      )}
      <TableHead><SortableHeader sort={sort} k="name" testIdPrefix="library">{t('library.list.th.name', { defaultValue: 'Name' })}</SortableHeader></TableHead>
      <TableHead><SortableHeader sort={sort} k="code" testIdPrefix="library">{t('library.list.th.code', { defaultValue: 'Code' })}</SortableHeader></TableHead>
      <TableHead>{t('library.list.th.suite', { defaultValue: 'Suite' })}</TableHead>
      <TableHead><SortableHeader sort={sort} k="status" testIdPrefix="library">{t('library.list.th.status', { defaultValue: 'Status' })}</SortableHeader></TableHead>
      <TableHead>{t('library.list.th.type', { defaultValue: 'Tipo' })}</TableHead>
      <TableHead><SortableHeader sort={sort} k="versions" testIdPrefix="library">{t('library.list.th.versions', { defaultValue: 'Versions' })}</SortableHeader></TableHead>
      <TableHead><SortableHeader sort={sort} k="author" testIdPrefix="library">{t('library.list.th.author', { defaultValue: 'Author' })}</SortableHeader></TableHead>
      <TableHead>{t('library.list.th.origin', { defaultValue: 'Origin' })}</TableHead>
      <TableHead>{t('library.list.th.usage', { defaultValue: 'Usage' })}</TableHead>
      <TableHead><SortableHeader sort={sort} k="updated" testIdPrefix="library">{t('library.list.th.updated', { defaultValue: 'Last modified' })}</SortableHeader></TableHead>
      <TableHead className="text-right">{t('common.actions', { defaultValue: 'Actions' })}</TableHead>
    </TableRow>
  );

  const bulkStats = confirmDel?.bulk ? {
    total: confirmDel.rows.length,
    deletable: confirmDel.rows.filter((r) => r.dep.safe).length,
    blocked: confirmDel.rows.filter((r) => !r.dep.safe).length,
  } : null;

  return (
    <div className="space-y-4" data-testid="library-list-view">
      <Card className="p-4 shadow-card flex flex-wrap items-center gap-3" data-testid="library-list-toolbar">
        <div className="relative flex-1 min-w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setParam('q', e.target.value); }} className="pl-9 h-9"
                 placeholder={t('library.list.searchPh', { defaultValue: 'Search by name or code…' })} data-testid="library-list-search" />
        </div>
        <div className="flex items-center gap-1.5" data-testid="library-status-chips">
          {STATUS_CHIPS.map((s) => (
            <button key={s} onClick={() => onStatusFilter(s)} data-testid={`library-chip-${s}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition ${statusFilter === s ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted/40'}`}>
              {s === 'all' ? t('library.list.chipAll', { defaultValue: 'All' })
                : s === 'pending' ? t('library.list.chipPending', { defaultValue: 'In review' })
                  : statusLabel(s)}
            </button>
          ))}
        </div>
        <Select value={suiteF} onValueChange={(v) => setParam('suite', v)}>
          <SelectTrigger className="h-9 w-44" data-testid="library-list-filter-suite"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('library.list.suiteAll', { defaultValue: 'Suite: all' })}</SelectItem>
            {[...new Set(enriched.flatMap((it) => it.suite_codes || []))].sort().map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={langF} onValueChange={(v) => setParam('language', v)}>
          <SelectTrigger className="h-9 w-36" data-testid="library-list-filter-language"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('library.list.langAll', { defaultValue: 'Language: all' })}</SelectItem>
            {[...new Set(enriched.map((it) => it._lang).filter(Boolean))].sort().map((l) => (
              <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={origin} onValueChange={setOrigin}>
          <SelectTrigger className="h-9 w-44" data-testid="library-filter-origin"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('library.list.originAll', { defaultValue: 'Origin: all' })}</SelectItem>
            <SelectItem value="manual">{originLabel('manual')}</SelectItem>
            <SelectItem value="uat">{originLabel('uat')}</SelectItem>
            <SelectItem value="auto_test">{originLabel('auto_test')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={used} onValueChange={setUsed}>
          <SelectTrigger className="h-9 w-44" data-testid="library-filter-used"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('library.list.usedAll', { defaultValue: 'Usage: all' })}</SelectItem>
            <SelectItem value="used">{t('library.list.usedOnly', { defaultValue: 'Used' })}</SelectItem>
            <SelectItem value="unused">{t('library.list.neverUsed', { defaultValue: 'Never used' })}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger className="h-9 w-44" data-testid="library-group-by"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t('library.list.groupNone', { defaultValue: 'No grouping' })}</SelectItem>
            <SelectItem value="type">{t('library.list.groupType', { defaultValue: 'Group: type' })}</SelectItem>
            <SelectItem value="suite">{t('library.list.groupSuite', { defaultValue: 'Group: suite' })}</SelectItem>
            <SelectItem value="status">{t('library.list.groupStatus', { defaultValue: 'Group: status' })}</SelectItem>
            <SelectItem value="language">{t('library.list.groupLanguage', { defaultValue: 'Group: language' })}</SelectItem>
            <SelectItem value="origin">{t('library.list.groupOrigin', { defaultValue: 'Group: origin' })}</SelectItem>
            <SelectItem value="author">{t('library.list.groupAuthor', { defaultValue: 'Group: author' })}</SelectItem>
          </SelectContent>
        </Select>
        {isAdmin && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="library-show-archived">
            <Switch checked={showArchived} onCheckedChange={(v) => { setShowArchived(!!v); if (v) onStatusFilter('archived'); else onStatusFilter('all'); }} />
            {t('library.list.showArchived', { defaultValue: 'Mostrar archivadas' })}
          </label>
        )}
        <Badge variant="outline" className="tabular-nums" data-testid="library-list-count">{sorted.length}</Badge>
      </Card>

      {isAdmin && selIds.length > 0 && (
        <Card className="p-3 shadow-card flex items-center gap-3 border-primary/40 bg-primary/5" data-testid="library-bulk-bar">
          <span className="text-sm font-medium">{t('library.list.selected', { defaultValue: '{{n}} selected', n: selIds.length })}</span>
          <Button size="sm" variant="outline" disabled={busy} data-testid="library-bulk-archive"
                  onClick={async () => {
                    setBusy(true);
                    let ok = 0;
                    for (const id of selIds) {
                      try { await api.post(`/admin/archive/questionnaire/${id}`, { reason_code: 'manual_archive' }); ok += 1; } catch { /* skip */ }
                    }
                    setBusy(false); setSelected({});
                    toast.success(t('library.list.bulkArchived', { defaultValue: '{{n}} evaluations archived.', n: ok }));
                    reload();
                  }}>
            <Archive className="h-3.5 w-3.5 mr-1" /> {t('library.list.archiveSelected', { defaultValue: 'Archive selected' })}
          </Button>
          <Button size="sm" variant="outline" className="text-destructive border-destructive/40" disabled={busy}
                  onClick={bulkPreflight} data-testid="library-bulk-delete">
            <Trash2 className="h-3.5 w-3.5 mr-1" /> {t('library.list.deleteSafe', { defaultValue: 'Delete safe drafts' })}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected({})}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
        </Card>
      )}

      <Card className="shadow-card">
        {sorted.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground" data-testid="library-list-empty">
            {t('library.list.empty', { defaultValue: 'No evaluations match the current filters.' })}
          </div>
        ) : (
          <SyncedHScroll testId="library-list-scroll">
          <Table containerClassName="!overflow-visible" data-testid="library-list-table">
            <TableHeader>{headers}</TableHeader>
            <TableBody>
              {groups.map((g) => (
                <React.Fragment key={g.key ?? '_all'}>
                  {g.key !== null && (
                    <TableRow className="bg-muted/40">
                      <TableCell colSpan={isAdmin ? 12 : 11} className="py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {groupBy === 'status' ? statusLabel(g.key)
                          : groupBy === 'type' ? typeLabel(g.key)
                            : groupBy === 'language' ? langLabel(g.key)
                              : groupBy === 'origin' ? originLabel(g.key) : g.key} · {g.rows.length}
                      </TableCell>
                    </TableRow>
                  )}
                  {g.rows.map(renderRow)}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
          </SyncedHScroll>
        )}
      </Card>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="max-w-md" data-testid="library-delete-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-destructive" />
              {t('library.list.confirmTitle', { defaultValue: 'Permanent deletion' })}
            </DialogTitle>
          </DialogHeader>
          {confirmDel?.bulk ? (
            <div className="space-y-2 text-sm">
              <p data-testid="library-bulk-summary">
                {t('library.list.bulkSummary', {
                  defaultValue: 'Selected: {{total}} · Deletable: {{deletable}} · Blocked (archive only): {{blocked}}',
                  total: bulkStats.total, deletable: bulkStats.deletable, blocked: bulkStats.blocked,
                })}
              </p>
              <p className="text-muted-foreground">
                {t('library.list.bulkNote', { defaultValue: 'Only records that have never been used and have no dependencies will be deleted. A backup and an audit entry are created for each one. This action is permanent.' })}
              </p>
            </div>
          ) : confirmDel && (
            <p className="text-sm text-muted-foreground">
              {t('library.list.confirmSafe', {
                defaultValue: '"{{name}}" has never been used and has no dependencies. A backup will be stored and the deletion will be audited. This action is permanent.',
                name: confirmDel.item?.name,
              })}
            </p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDel(null)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button variant="destructive" disabled={busy || (confirmDel?.bulk && bulkStats.deletable === 0)}
                    onClick={executeDelete} data-testid="library-delete-confirm-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('library.list.confirmBtn', { defaultValue: 'Delete permanently' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
